import { useId } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { ASSISTANT_ASK_BAR, ASSISTANT_NAME } from './copy'
import type { AssistantNudge } from './nudge'
import SparkleIcon from './SparkleIcon'

interface AssistantNudgeCardProps {
  nudge: AssistantNudge
  onReview: () => void
  onDismiss: () => void
  /**
   * popover: the card that drops from the top-bar button. inline: "Needs
   * you", inside the top-bar dropdown and the panel's empty state.
   */
  variant?: 'popover' | 'inline'
  /** Id on the sentence, so the top-bar button can be described by it. */
  messageId?: string
}

/**
 * The proactive nudge: one sentence about an urgent task, with Review and
 * Not Now. It stays until one of them is chosen.
 */
const AssistantNudgeCard = ({
  nudge,
  onReview,
  onDismiss,
  variant = 'popover',
  messageId,
}: AssistantNudgeCardProps) => {
  const titleId = useId()
  const body = (
    <>
      <Typography variant="body2" id={messageId}>
        {nudge.message}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" size="small" onClick={onReview}>
          Review
        </Button>
        <Button variant="text" size="small" onClick={onDismiss}>
          Not Now
        </Button>
      </Box>
    </>
  )

  if (variant === 'inline') {
    return (
      <Box
        role="group"
        aria-labelledby={titleId}
        data-testid="assistant-needs-you"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 1.5,
          bgcolor: 'secondary.lightest',
          border: 1,
          borderColor: 'secondary.light',
          borderRadius: 1,
          textAlign: 'left',
        }}
      >
        <Typography id={titleId} variant="captionBold" color="primary">
          {ASSISTANT_ASK_BAR.needsYou}
        </Typography>
        {body}
      </Box>
    )
  }

  return (
    <Paper
      role="region"
      aria-label={`${ASSISTANT_NAME} suggestion`}
      data-testid="assistant-nudge"
      elevation={4}
      sx={{
        width: 320,
        maxWidth: 'calc(100vw - 32px)',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SparkleIcon small color="primary" sx={{ fontSize: 16 }} />
        <Typography variant="caption" fontWeight={700} color="primary">
          {ASSISTANT_NAME}
        </Typography>
      </Box>
      {body}
    </Paper>
  )
}

export default AssistantNudgeCard
